"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const compact_1 = __importDefault(require("lodash/compact"));
const mapValues_1 = __importDefault(require("lodash/mapValues"));
const reselect_1 = require("reselect");
const questionnaire_1 = require("@zedoc/questionnaire");
const selectors_1 = require("@zedoc/selectors");
const selectors_2 = require("./selectors");
const actions_1 = require("./actions");
const selectState = (state) => state.questionnaire;
const createValuesSelector = (selectName, field) => (0, reselect_1.createSelector)(selectState, selectName, (state, name) => state && state[name] && state[name][field]);
class SelectorsHub {
    constructor(props) {
        const { name, questionnaire, properties, variables, flatSections, answersSheetId, sessionId, debounceEdit, disabled, } = props;
        const selectName = (0, selectors_1.toSelector)(name);
        const selectQuestionnaire = (0, selectors_1.toSelector)(questionnaire);
        const selectPropertiesOverrides = (0, selectors_1.toSelector)(properties);
        const selectFlatSections = (0, selectors_1.toSelector)(flatSections);
        const selectVariables = (0, selectors_1.toSelector)(variables);
        const selectAnswersSheetId = (0, selectors_1.toSelector)(answersSheetId);
        const selectSessionId = (0, selectors_1.toSelector)(sessionId);
        const selectRawFormValues = createValuesSelector(selectName, 'values');
        const selectRawTouched = createValuesSelector(selectName, 'touched');
        const selectValidationErrors = createValuesSelector(selectName, 'errors');
        this.select = (0, selectors_2.createDynamicQuestionnaireSelectors)({
            selectRawFormValues,
            selectPropertiesOverrides,
            selectRawTouched,
            selectVariables,
            selectValidationErrors,
            selectQuestionnaire,
            selectFlatSections,
        });
        this.select.context = (0, selectors_1.constant)((0, selectors_1.constant)(this));
        this.select.name = (0, selectors_1.constant)(selectName);
        this.select.options = (0, selectors_1.constant)((0, selectors_1.toSelector)({
            debounceEdit,
            disabled,
        }));
        this.select.meta = (0, selectors_1.constant)((0, selectors_1.toSelector)({
            name: selectName,
            rawQuestionnaire: (0, reselect_1.createSelector)(selectQuestionnaire, (doc) => doc && doc.raw),
            variables: selectVariables,
            properties: selectPropertiesOverrides,
            sessionId: selectSessionId,
            answersSheetId: selectAnswersSheetId,
        }));
    }
    getBasePath(path) {
        return (0, compact_1.default)([this.scopeKey, this.questionId, path]).join('.');
    }
    getScopeKey(elementId) {
        if (this.scopeKey) {
            return `${this.scopeKey}.${this.questionId}._elements.${elementId}._elements`;
        }
        return `${this.questionId}._elements.${elementId}._elements`;
    }
    createPropertiesSelector(questionId = this.questionId) {
        const key = (0, compact_1.default)([this.scopeKey, questionId]).join('.');
        return (0, reselect_1.createSelector)(this.select.dynamicProperties(), (0, selectors_1.createGetAtKey)(key));
    }
    createFormValueSelector(name = 'value') {
        const key = this.getBasePath(name);
        return (0, reselect_1.createSelector)(this.select.formValues(), (0, selectors_1.createGetAtKey)(key));
    }
    createValidationErrorSelector(name = 'value') {
        const key = this.getBasePath(name);
        return (0, reselect_1.createSelector)(this.select.validationErrors(), (0, selectors_1.createGetAtKey)(key));
    }
    createTouchedSelector(name = 'value') {
        const key = this.getBasePath(name);
        return (0, reselect_1.createSelector)(this.select.touched(), (0, selectors_1.createGetAtKey)(key));
    }
    createSubContext(elementId) {
        const newContext = Object.assign(Object.create(this), {
            question: null,
            questionId: null,
            scopeKey: this.getScopeKey(elementId),
        });
        return newContext;
    }
    bindProps(props) {
        const newSelectors = (0, mapValues_1.default)(this.select, (selectorCreator) => (...args) => {
            const selector = selectorCreator(...args);
            return (state) => selector(state, props);
        });
        const newContext = Object.assign(Object.create(this), {
            select: newSelectors,
        });
        return newContext;
    }
    setValue(field, value, source = questionnaire_1.RESPONSE_SOURCE__USER_INPUT) {
        return (dispatch, getState) => {
            const state = getState();
            const meta = this.select.meta()(state);
            return dispatch({
                type: actions_1.ACTION_SET_VALUE,
                payload: value,
                meta: Object.assign(Object.assign({}, meta), { field, key: this.getBasePath(), source, ts: Date.now() }),
            });
        };
    }
    skipAnswer({ source = questionnaire_1.RESPONSE_SOURCE__USER_INPUT } = {}) {
        return (dispatch, getState) => {
            const state = getState();
            const meta = this.select.meta()(state);
            return dispatch({
                type: actions_1.ACTION_SKIP_ANSWER,
                // NOTE: At a later stage we may decide to use other values
                //       based on current question type, e.g. [], or "".
                payload: null,
                meta: Object.assign(Object.assign({}, meta), { key: this.getBasePath(), source, ts: Date.now() }),
            });
        };
    }
    insertElement(elementId, questionId, values = {}) {
        return (dispatch, getState) => {
            const state = getState();
            const meta = this.select.meta()(state);
            return dispatch({
                type: actions_1.ACTION_INSERT_ELEMENT,
                payload: values,
                meta: Object.assign(Object.assign({}, meta), { elementId, key: this.getBasePath(questionId) }),
            });
        };
    }
    removeElement(elementId, questionId) {
        return (dispatch, getState) => {
            const state = getState();
            const meta = this.select.meta()(state);
            return dispatch({
                type: actions_1.ACTION_REMOVE_ELEMENT,
                payload: elementId,
                meta: Object.assign(Object.assign({}, meta), { key: this.getBasePath(questionId) }),
            });
        };
    }
    moveElementUp(elementId, questionId) {
        return (dispatch, getState) => {
            const state = getState();
            const meta = this.select.meta()(state);
            return dispatch({
                type: actions_1.ACTION_MOVE_ELEMENT_UP,
                payload: elementId,
                meta: Object.assign(Object.assign({}, meta), { key: this.getBasePath(questionId) }),
            });
        };
    }
    moveElementDown(elementId, questionId) {
        return (dispatch, getState) => {
            const state = getState();
            const meta = this.select.meta()(state);
            return dispatch({
                type: actions_1.ACTION_MOVE_ELEMENT_DOWN,
                payload: elementId,
                meta: Object.assign(Object.assign({}, meta), { key: this.getBasePath(questionId) }),
            });
        };
    }
    validate(dryRun = false) {
        return (dispatch, getState) => {
            const state = getState();
            const meta = this.select.meta()(state);
            return dispatch({
                meta: Object.assign(Object.assign({}, meta), { dryRun }),
                type: actions_1.ACTION_VALIDATE_QUESTIONNAIRE,
            });
        };
    }
    setErrors(payload) {
        return (dispatch, getState) => {
            const state = getState();
            const meta = this.select.meta()(state);
            return dispatch({
                meta,
                payload,
                type: actions_1.ACTION_REPLACE_FORM_ERRORS,
            });
        };
    }
    forceAutosave() {
        return (dispatch, getState) => {
            const state = getState();
            const meta = this.select.meta()(state);
            return dispatch({
                meta,
                type: actions_1.ACTION_FORCE_AUTOSAVE,
            });
        };
    }
    syncFormValues(payload) {
        return (dispatch, getState) => {
            const state = getState();
            const meta = this.select.meta()(state);
            return dispatch({
                meta,
                payload,
                type: actions_1.ACTION_SYNC_FORM_VALUES,
            });
        };
    }
}
exports.default = SelectorsHub;
//# sourceMappingURL=SelectorsHub.js.map